import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Furniture & Appliance Removal",
  "Garage / Estate / House Cleanout",
  "Yard & Construction Debris",
  "Shed Demolition",
  "Hot Tub Removal",
  "Something Else",
];

const CITIES = ["Cushing", "Stillwater", "Perkins", "Yale", "Ripley", "Glencoe", "Payne County — Other", "Noble County", "Pawnee County", "Creek County", "Logan County"];

const LOADS = ["Single Item", "1/4 Truck", "1/2 Truck", "Full Truckload"];

const EMPTY = { name: "", phone: "", email: "", service: SERVICES[0], city: CITIES[0], load_size: "Single Item", message: "" };

const inputCls =
  "w-full border border-[#282C34] bg-[#0B0C0E] px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#FF5A00]";

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received! We'll call you back fast.", {
        description: "Need it sooner? Call 918-399-7313 now.",
      });
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong — please call us at 918-399-7313.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
      <div className="grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <Reveal>
          <p className="font-mono-ind text-xs uppercase tracking-[0.3em] text-[#FF9F00]">
            Free Quote
          </p>
          <h2 className="mt-4 font-display text-5xl leading-[0.95] tracking-tight sm:text-7xl">
            LET'S HAUL
            <br />
            <span className="text-[#FF5A00]">YOUR JUNK.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base">
            Tell us what needs to go and where you are — we'll get back to you
            fast with a fair, upfront price. Prefer to talk? Call or text{" "}
            <a href="tel:+19183997313" data-testid="quote-phone-link" className="font-bold text-[#FF9F00] underline-offset-4 hover:underline">
              918-399-7313
            </a>
            .
          </p>

          <div className="mt-10 border border-[#282C34] bg-[#14171C] p-6">
            <p className="font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Rough load size
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="load-size-picker">
              {LOADS.map((l) => (
                <button
                  key={l}
                  type="button"
                  data-testid={`load-size-${l.toLowerCase().replace(/[\s/]+/g, "-")}`}
                  onClick={() => setForm({ ...form, load_size: l })}
                  className={`border px-4 py-2 font-mono-ind text-xs uppercase tracking-widest transition-all duration-300 ${
                    form.load_size === l
                      ? "border-[#FF5A00] bg-[#FF5A00]/15 text-[#FF9F00]"
                      : "border-[#282C34] text-zinc-400 hover:border-[#FF5A00]/50"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="relative border border-[#282C34] bg-[#14171C] p-6 sm:p-10"
          >
            <div className="hazard absolute top-0 left-0 right-0 h-1 opacity-70" />
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="q-name" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Full Name *
                </label>
                <input id="q-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="John Garrett" className={inputCls} />
              </div>
              <div>
                <label htmlFor="q-phone" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Phone *
                </label>
                <input id="q-phone" data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(918) 555-0123" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="q-email" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Email (optional)
                </label>
                <input id="q-email" data-testid="quote-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="q-service" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Service Needed *
                </label>
                <select id="q-service" data-testid="quote-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                  {SERVICES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="q-city" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  City *
                </label>
                <select id="q-city" data-testid="quote-city-select" value={form.city} onChange={set("city")} className={inputCls}>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="q-message" className="mb-2 block font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  What are we hauling?
                </label>
                <textarea id="q-message" data-testid="quote-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="e.g. Old sectional sofa, a broken fridge, and some brush piles in the backyard..." className={`${inputCls} resize-none`} />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-button"
              className="glow-orange group mt-8 flex w-full items-center justify-center gap-3 bg-[#FF5A00] py-4 font-display text-2xl tracking-widest text-[#0B0C0E] transition-all duration-300 hover:bg-[#FF9F00] disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  REQUEST MY FREE QUOTE
                  <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
            <p className="mt-4 text-center font-mono-ind text-[10px] uppercase tracking-widest text-zinc-600">
              No obligation · Fast callback · Fair upfront pricing
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
